import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { medicalConditionsSchema } from "~/schemas/SignUp/InsuranceProvider";
import { useSubStepTracking } from "~/shared/hooks/useFunnelTracking";
import {
  INSURANCE_MEDICAL_CONDITIONS,
  INSURANCE_PROVIDERS,
  NONE_OF_THESE,
  PLAN_TYPE_PROVIDERS,
} from "~/shared/utils/constants";
import { InsuranceProviderFormProps } from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { ActionButtons } from "../../ActionButtons";
import { ErrorMessage } from "../../ErrorMessage";
import { FormContentContainer } from "../../FormContentContainer";

export const MedicalConditionsForm = ({
  onPrev,
  onNext,
  isSecondaryProvider = false,
}: InsuranceProviderFormProps) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const { patient, setPatientInsuranceField } = useSignUpStore();
  const { primaryInsurance, secondaryInsurance } = patient;

  const insurance = isSecondaryProvider ? secondaryInsurance : primaryInsurance;
  const isAetnaMedicareAdvantage =
    insurance?.providerName?.toLowerCase() ===
      INSURANCE_PROVIDERS.aetna.toLowerCase() &&
    insurance?.planTypeProvider?.toLowerCase() ===
      PLAN_TYPE_PROVIDERS.medicare.toLowerCase();

  const visibleConditions = INSURANCE_MEDICAL_CONDITIONS.filter(
    (condition) =>
      condition.toLowerCase() !== "obesity" || isAetnaMedicareAdvantage,
  );

  const defaultValue = isSecondaryProvider
    ? secondaryInsurance?.medicalConditions
    : primaryInsurance.medicalConditions;

  const { trackComplete, trackBack, trackErrors } = useSubStepTracking({
    parentStep: isSecondaryProvider
      ? "secondary_insurance"
      : "primary_insurance",
    subStep: "medical_conditions", // see table below
  });

  useEffect(() => {
    setSelectedOptions(defaultValue ?? []);
  }, [defaultValue]);

  const {
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof medicalConditionsSchema>>({
    mode: "onBlur",
    resolver: zodResolver(medicalConditionsSchema),
    defaultValues: {
      medicalConditions: defaultValue ?? [],
    },
  });

  useEffect(() => {
    setValue("medicalConditions", defaultValue ?? []);
  }, [defaultValue, setValue]);

  const toggleCheckbox = (option: string) => {
    if (option === NONE_OF_THESE) {
      if (selectedOptions.includes(NONE_OF_THESE)) {
        setSelectedOptions([]);
        setValue("medicalConditions", [], { shouldValidate: true });
      } else {
        setSelectedOptions([NONE_OF_THESE]);
        setValue("medicalConditions", [NONE_OF_THESE], {
          shouldValidate: true,
        });
      }
    } else {
      const filteredOptions = selectedOptions.filter(
        (item) => item !== NONE_OF_THESE,
      );

      if (selectedOptions.includes(option)) {
        setSelectedOptions(filteredOptions.filter((item) => item !== option));
        setValue(
          "medicalConditions",
          filteredOptions.filter((item) => item !== option),
          { shouldValidate: true },
        );
      } else {
        setSelectedOptions([...filteredOptions, option]);
        setValue("medicalConditions", [...filteredOptions, option], {
          shouldValidate: true,
        });
      }
    }
  };

  const onSubmit: SubmitHandler<z.infer<typeof medicalConditionsSchema>> = (
    data,
  ) => {
    setPatientInsuranceField(
      "medicalConditions",
      data.medicalConditions,
      isSecondaryProvider,
    );

    trackComplete({ medical_conditions: data.medicalConditions });

    onNext();
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientInsuranceField(
      "medicalConditions",
      undefined,
      isSecondaryProvider,
    );
    trackBack();
    onPrev();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="rounded-lg shadow-xl"
    >
      <FormContentContainer
        stepId={
          isSecondaryProvider ? "secondary_insurance" : "primary_insurance"
        }
      >
        <HipaaLogo className="size-20 self-center" />
        <div>
          <h1 className="font-display text-2xl">
            Do you have any of the following medical conditions? Select all that
            apply.
          </h1>
        </div>
        <div className="flex flex-col gap-2">
          {visibleConditions.map((option) => (
            <div key={option} className="flex items-start">
              <input
                type="checkbox"
                id={`option-${option}`}
                checked={selectedOptions.includes(option)}
                className="mt-1 size-4 cursor-pointer rounded"
                value={option}
                onChange={() => toggleCheckbox(option)}
              />
              <label
                htmlFor={`option-${option}`}
                className="ml-2 text-sm font-medium text-gray-900"
              >
                {option}
              </label>
            </div>
          ))}
        </div>
        {errors.medicalConditions && (
          <ErrorMessage message={errors.medicalConditions.message ?? ""} />
        )}
      </FormContentContainer>
      <ActionButtons onPrev={handlePrev} />
    </form>
  );
};
